// src/IAQSection.js
import React, { useRef } from 'react';
import { fileToDataURL } from './utils';

export default function IAQSection({ iaqReadings, updIAQ }) {
  const greenBtn = "bg-green-500 text-white px-3 py-1 rounded";
  const redBtn   = "bg-red-500 text-white px-3 py-1 rounded";

  // refs for the hidden “capture” inputs
  const tempCaptureRef = useRef(null);
  const pmCaptureRef   = useRef(null);
  const hchoCaptureRef = useRef(null);
  const co2CaptureRef  = useRef(null);

  // generic file → base64 → state
  const onPick = async (field, idx = 0, e) => {
    const f = e.target.files[0];
    if (!f) return;
    const url = await fileToDataURL(f);
    if (Array.isArray(iaqReadings[field])) {
      const arr = [...iaqReadings[field]];
      arr[idx] = url;
      updIAQ(field, arr);
    } else {
      updIAQ(field, url);
    }
    e.target.value = null;
  };

  // confirm + delete
  const onDelete = (field, idx = 0) => {
    if (!window.confirm('Delete this photo?')) return;
    if (Array.isArray(iaqReadings[field])) {
      const arr = iaqReadings[field].filter((_,i)=>i!==idx);
      updIAQ(field, arr);
    } else {
      updIAQ(field, null);
    }
  };

  return (
    <div className="space-y-6 mb-8">

      {/* Temperature */}
      <div>
        <label className="block font-medium mb-1">Temperature (°F):</label>
        <input
          type="text"
          className="w-full border rounded px-2 py-1"
          value={iaqReadings.temperature}
          onChange={e => updIAQ('temperature', e.target.value)}
        />
      </div>

      {/* Humidity */}
      <div>
        <label className="block font-medium mb-1">Humidity (%):</label>
        <input
          type="text"
          className="w-full border rounded px-2 py-1"
          value={iaqReadings.relativeHumidity}
          onChange={e => updIAQ('relativeHumidity', e.target.value)}
        />
      </div>

      {/* AQI */}
      <div>
        <label className="block font-medium mb-1">AQI:</label>
        <input
          type="text"
          className="w-full border rounded px-2 py-1"
          value={iaqReadings.aqi}
          onChange={e => updIAQ('aqi', e.target.value)}
        />
      </div>

      {/* Photo (Temp/RH/AQI) */}
      <div>
        <label className="block font-medium mb-1">Picture (Temp/RH/AQI):</label>
        <div className="flex items-center gap-4 mb-2">
          <input
            type="file"
            accept="image/*"
            onChange={e => onPick('temperatureHumidityPicture', 0, e)}
          />
          <input
            ref={tempCaptureRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={e => onPick('temperatureHumidityPicture', 0, e)}
          />
          <button
            className={greenBtn}
            onClick={() => tempCaptureRef.current?.click()}
          >
            Take Photo
          </button>
        </div>
        {iaqReadings.temperatureHumidityPicture && (
          <div>
            <img
              src={iaqReadings.temperatureHumidityPicture}
              alt=""
              className="max-w-xs mb-2 rounded"
            />
            <button
              className={redBtn}
              onClick={() => onDelete('temperatureHumidityPicture', 0)}
            >
              Delete Photo
            </button>
          </div>
        )}
      </div>

      {/* PM2.5 */}
      <div>
        <label className="block font-medium mb-1">PM2.5 (µg/m³):</label>
        <input
          type="text"
          className="w-full border rounded px-2 py-1"
          value={iaqReadings.pm25}
          onChange={e => updIAQ('pm25', e.target.value)}
        />
      </div>

      {/* PM10 */}
      <div>
        <label className="block font-medium mb-1">PM10 (µg/m³):</label>
        <input
          type="text"
          className="w-full border rounded px-2 py-1"
          value={iaqReadings.pm10}
          onChange={e => updIAQ('pm10', e.target.value)}
        />
      </div>

      {/* Particle Count */}
      <div>
        <label className="block font-medium mb-1">Particle Count (per L):</label>
        <input
          type="text"
          className="w-full border rounded px-2 py-1"
          value={iaqReadings.particleCount}
          onChange={e => updIAQ('particleCount', e.target.value)}
        />
      </div>

      {/* Photo (PM/Count) */}
      <div>
        <label className="block font-medium mb-1">Picture (PM/Count):</label>
        <div className="flex items-center gap-4 mb-2">
          <input
            type="file"
            accept="image/*"
            onChange={e => onPick('pmPicture', 0, e)}
          />
          <input
            ref={pmCaptureRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={e => onPick('pmPicture', 0, e)}
          />
          <button
            className={greenBtn}
            onClick={() => pmCaptureRef.current?.click()}
          >
            Take Photo
          </button>
        </div>
        {iaqReadings.pmPicture && (
          <div>
            <img
              src={iaqReadings.pmPicture}
              alt=""
              className="max-w-xs mb-2 rounded"
            />
            <button
              className={redBtn}
              onClick={() => onDelete('pmPicture', 0)}
            >
              Delete Photo
            </button>
          </div>
        )}
      </div>

      {/* Formaldehyde */}
      <div>
        <label className="block font-medium mb-1">Formaldehyde (mg/m³):</label>
        <input
          type="text"
          className="w-full border rounded px-2 py-1"
          value={iaqReadings.formaldehyde}
          onChange={e => updIAQ('formaldehyde', e.target.value)}
        />
      </div>

      {/* TVOC */}
      <div>
        <label className="block font-medium mb-1">TVOC (mg/m³):</label>
        <input
          type="text"
          className="w-full border rounded px-2 py-1"
          value={iaqReadings.tvoc}
          onChange={e => updIAQ('tvoc', e.target.value)}
        />
      </div>

      {/* Photo (HCHO/TVOC) */}
      <div>
        <label className="block font-medium mb-1">Picture (HCHO/TVOC):</label>
        <div className="flex items-center gap-4 mb-2">
          <input
            type="file"
            accept="image/*"
            onChange={e => onPick('formaldehydeTvocPictures', 0, e)}
          />
          <input
            ref={hchoCaptureRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={e => onPick('formaldehydeTvocPictures', 0, e)}
          />
          <button
            className={greenBtn}
            onClick={() => hchoCaptureRef.current?.click()}
          >
            Take Photo
          </button>
        </div>
        {iaqReadings.formaldehydeTvocPictures[0] && (
          <div>
            <img
              src={iaqReadings.formaldehydeTvocPictures[0]}
              alt=""
              className="max-w-xs mb-2 rounded"
            />
            <button
              className={redBtn}
              onClick={() => onDelete('formaldehydeTvocPictures', 0)}
            >
              Delete Photo
            </button>
          </div>
        )}
      </div>

      {/* CO₂ */}
      <div>
        <label className="block font-medium mb-1">CO₂ (ppm):</label>
        <input
          type="text"
          className="w-full border rounded px-2 py-1"
          value={iaqReadings.co2}
          onChange={e => updIAQ('co2', e.target.value)}
        />
      </div>

      {/* Photo (CO₂) */}
      <div>
        <label className="block font-medium mb-1">Picture (CO₂):</label>
        <div className="flex items-center gap-4 mb-2">
          <input
            type="file"
            accept="image/*"
            onChange={e => onPick('co2AqiPictures', 0, e)}
          />
          <input
            ref={co2CaptureRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={e => onPick('co2AqiPictures', 0, e)}
          />
          <button
            className={greenBtn}
            onClick={() => co2CaptureRef.current?.click()}
          >
            Take Photo
          </button>
        </div>
        {iaqReadings.co2AqiPictures[0] && (
          <div>
            <img
              src={iaqReadings.co2AqiPictures[0]}
              alt=""
              className="max-w-xs mb-2 rounded"
            />
            <button
              className={redBtn}
              onClick={() => onDelete('co2AqiPictures', 0)}
            >
              Delete Photo
            </button>
          </div>
        )}
      </div>
    </div>
  );
}